import React from 'react';
import loadable from '@loadable/component';
import { Switch } from 'react-router-dom';
import { public_urls } from '../config';
import ROLES from '../constants/role';
import { FORECAST, SASC, HOME } from '../constants/app';

import LoadingIndicator from '../components/common/LoadingIndicator';
import ProtectedRoute from '../components/common/protected/route';

import Layout from '../components/Layout';

const {
  reports,
  forecastQuoteAndDealList,
  forecastTotals,
  forecastSubscriptions,
  forecastFLMMap,
  qRadarPartDetails,
  adminPage,
  configurationTool,
  newConfiguration,
  applianceSN,
  createConfigurationError,
  termsOfUse,
  cadence,
} = public_urls;

const HomePage = loadable(() => import('../containers/HomePage'), {
  fallback: LoadingIndicator({ error: false }),
});

const AppliancesPage = loadable(() => import('../containers/ApplianceSerialNumber'), {
  fallback: LoadingIndicator({ error: false }),
});

const ForecastTotalsPage = loadable(() => import('../containers/TotalsPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const ForecastQuoteAndDealListPage = loadable(() => import('../containers/QuoteAndDealListPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const ForecastSubscriptionsPage = loadable(() => import('../containers/SubscriptionsPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const ForecastFLMMapPage = loadable(() => import('../containers/ForecastFLMMapPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const ReportsPage = loadable(() => import('../containers/ReportsPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const QRadarPartDetailsPage = loadable(() => import('../containers/QRadarPartDetailsPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const AdminPage = loadable(() => import('../containers/AdminPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const TasksPage = loadable(() => import('../containers/TasksPage'), {
  fallback: LoadingIndicator({ error: false }),
});

const NewConfigurationPage = loadable(() => import('../containers/ConfigurationPages/NewConfigurationPage'), {
  fallback: LoadingIndicator({
    error: false,
  }),
});

const CreateConfigurationErrorPage = loadable(
  () => import('../containers/ConfigurationPages/errorPages/CreateConfigurationErrorPage'),
  {
    fallback: LoadingIndicator({
      error: false,
    }),
  },
);

const TermsOfUsePage = loadable(() => import('../containers/TermOfUsePage'), {
  fallback: LoadingIndicator({ error: false }),
});

const CadencePage = loadable(() => import('../containers/CadencePage'), {
  fallback: LoadingIndicator({ error: false }),
});

function Routes({ user }) {
  const forecastToolReadOnlyMode = user ? user.userTeams.indexOf(ROLES.FORECAST_EDITOR) === -1 : true;
  const cadenceToolReadOnlyMode = user ? user.userTeams.indexOf(ROLES.CADENCE_EDITOR) === -1 : true;
  return (
    <Switch>
      <ProtectedRoute
        exact
        path={forecastTotals}
        requiredAnyRoles={[ROLES.FORECAST_VIEWER, ROLES.FORECAST_EDITOR]}
        saveUrlOnFail
      >
        <Layout appSettings={FORECAST} user={user}>
          <ForecastTotalsPage readOnly={forecastToolReadOnlyMode} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute
        exact
        path={forecastQuoteAndDealList}
        requiredAnyRoles={[ROLES.FORECAST_VIEWER, ROLES.FORECAST_EDITOR]}
        saveUrlOnFail
      >
        <Layout appSettings={FORECAST} user={user}>
          <ForecastQuoteAndDealListPage readOnly={forecastToolReadOnlyMode} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={forecastSubscriptions} requiredAnyRoles={[ROLES.FORECAST_EDITOR]} saveUrlOnFail>
        <Layout appSettings={FORECAST} user={user}>
          <ForecastSubscriptionsPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={forecastFLMMap} requiredAnyRoles={[ROLES.FORECAST_EDITOR]} saveUrlOnFail>
        <Layout appSettings={FORECAST} user={user}>
          <ForecastFLMMapPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={qRadarPartDetails} requiredRoles={ROLES.ADMIN} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <QRadarPartDetailsPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={applianceSN} requiredRoles={ROLES.SELLER} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <AppliancesPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute
        exact
        path={cadence}
        requiredAnyRoles={[ROLES.CADENCE_EDITOR, ROLES.CADENCE_VIEWER]}
        saveUrlOnFail
      >
        <Layout appSettings={SASC} user={user}>
          <CadencePage readOnly={cadenceToolReadOnlyMode} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute
        path={reports.base}
        requiredAnyRoles={[ROLES.ADMIN, ROLES.ENTITLEMENT_ADMIN, ROLES.HW_REFRESH_ADMIN]}
        saveUrlOnFail
      >
        <Layout appSettings={SASC} user={user}>
          <ReportsPage user={user} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={adminPage} requiredRoles={ROLES.ADMIN} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <AdminPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={termsOfUse} requiredAnyRoles={[ROLES.SELLER, ROLES.BP]} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <TermsOfUsePage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={configurationTool} requiredAnyRoles={[ROLES.SELLER, ROLES.BP]} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <TasksPage user={user} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={newConfiguration} requiredAnyRoles={[ROLES.SELLER, ROLES.BP]} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <NewConfigurationPage />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute>
        <Layout appSettings={HOME} user={user} overflow="auto">
          <HomePage user={user} />
        </Layout>
      </ProtectedRoute>

      <ProtectedRoute exact path={createConfigurationError} requiredAnyRoles={[ROLES.SELLER, ROLES.BP]} saveUrlOnFail>
        <Layout appSettings={SASC} user={user}>
          <CreateConfigurationErrorPage />
        </Layout>
      </ProtectedRoute>
    </Switch>
  );
}

export default Routes;
